import { motion } from 'framer-motion';
import { 
  ShieldCheckIcon, 
  RocketLaunchIcon, 
  UserGroupIcon, 
  LightBulbIcon 
} from '@heroicons/react/24/outline';

const features = [
  {
    title: "Privacy First",
    description: "We care deeply about privacy and the transformative potential of Zero-Knowledge proofs in shaping a more secure digital future.",
    icon: ShieldCheckIcon,
    code: "0x7F1C"
  },
  {
    title: "Accelerated Growth",
    description: "Help companies leap frog in their roadmap with our cohesive ZK support system, from POC to MVP and beyond.",
    icon: RocketLaunchIcon,
    code: "0x2D9A"
  },
  {
    title: "Global Community",
    description: "Access our vast network of developers and ecosystem support individuals who are passionate about advancing ZK technology.",
    icon: UserGroupIcon,
    code: "0x4B3E"
  },
  {
    title: "Ecosystem Support",
    description: "Comprehensive support including specialized ZK marketers and strategic connections to accelerate your growth.",
    icon: LightBulbIcon,
    code: "0x9F5D"
  }
];

const Features = () => {
  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto relative">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(99,102,241,0.05),transparent_50%)]" />
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="text-center mb-16 relative"
      >
        <div className="absolute -top-8 left-1/2 -translate-x-1/2 text-xs font-mono text-indigo-500/40">
          [ENCRYPTED_TRANSMISSION_START]
        </div>
        <h2 className="text-4xl font-bold mb-6 text-white font-mono">
          Making ZK by-default standard
        </h2>
        <p className="text-lg text-slate-400 max-w-2xl mx-auto font-mono">
          Consumers are more privacy aware and they won't settle for any less - we help businesses achieve their goals and still provide the privacy and security to their users
        </p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {features.map((feature, index) => (
          <motion.div
            key={feature.title}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            whileHover={{ scale: 1.02 }}
            className="relative group"
          >
            <div className="absolute inset-0 bg-slate-800/50 rounded-lg backdrop-blur-sm border border-slate-700/50 
              group-hover:border-indigo-500/50 transition-colors duration-300" />
            
            {/* Decorative grid pattern */}
            <div className="absolute inset-0 opacity-[0.02] bg-[linear-gradient(to_right,#666_1px,transparent_1px),linear-gradient(to_bottom,#666_1px,transparent_1px)] bg-[size:4px_4px]" />
            
            <div className="relative p-8 h-full">
              <div className="flex items-center justify-between mb-4">
                <feature.icon className="w-8 h-8 text-indigo-400 group-hover:text-indigo-300 transition-colors" />
                <div className="font-mono text-xs text-indigo-500/60 group-hover:text-indigo-400/80 transition-colors">
                  {feature.code}
                </div>
              </div>
              
              <h3 className="text-xl font-bold text-white mb-3 font-mono group-hover:text-indigo-300 transition-colors">
                {feature.title}
              </h3>
              
              <p className="text-slate-400 font-mono leading-relaxed">
                {feature.description}
              </p>

              {/* Corner decorations */}
              <div className="absolute top-0 right-0 w-8 h-8 border-t border-r border-indigo-500/20 rounded-tr-lg" />
              <div className="absolute bottom-0 left-0 w-8 h-8 border-b border-l border-indigo-500/20 rounded-bl-lg" />
            </div>
          </motion.div>
        ))}
      </div>

      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 text-xs font-mono text-indigo-500/40">
        [ENCRYPTED_TRANSMISSION_END]
      </div>
    </section>
  );
};

export default Features;